import Divider from '@mui/material/Divider'
import ListSubheader from '@mui/material/ListSubheader'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import type { CSSObject } from '@mui/material/styles'

import {
  colors,
  componentTokens,
  shadows,
  typography
} from '../../KestrixDesignSystem.jsx'
import type { DropdownOptionSpacing } from '../dropdown-option.tokens.ts'
import { dropdownOptionTokens } from '../dropdown-option.tokens.ts'
import {
  isDividerOption,
  isGroupHeaderOption
} from '../dropdown-option-field.shared.ts'
import { DROPDOWN_OVERLAY, resolveOptionTextColor } from '../styles'
import type { DropdownOption, NavigationMenuPlacement } from '../types'
import { DropdownOptionField } from './DropdownOptionField'

const bodyMediumTokens = typography.body.medium

const getOverlayPaperSx = (placement: NavigationMenuPlacement) => ({
  // The popover places the paper by its top edge, so lifting it takes a negative top margin.
  mt: `${placement === 'above' ? -DROPDOWN_OVERLAY.menuGap : DROPDOWN_OVERLAY.menuGap}px`,
  padding: `${DROPDOWN_OVERLAY.menuPadding}px`,
  borderRadius: componentTokens.input.borderRadius,
  border: `1px solid ${colors.border.light}`,
  background: colors.background.secondary,
  boxShadow: shadows.dropdown,
  '& .MuiList-root': {
    padding: 0
  }
})

function getNavigationMenuItemSx(
  spacing: DropdownOptionSpacing,
  isSelected: boolean
): CSSObject {
  return {
    borderRadius: `${dropdownOptionTokens.borderRadius}px`,
    padding: dropdownOptionTokens.spacing[spacing],
    fontFamily: typography.fontFamily.primary,
    fontSize: bodyMediumTokens.fontSize,
    lineHeight: bodyMediumTokens.lineHeight,
    letterSpacing: bodyMediumTokens.letterSpacing,
    fontWeight: isSelected
      ? DROPDOWN_OVERLAY.itemSelectedFontWeight
      : bodyMediumTokens.fontWeight,
    color: dropdownOptionTokens.colors.textDefault,
    border: isSelected
      ? `1px solid ${dropdownOptionTokens.colors.borderSelected}`
      : '1px solid transparent',
    '&:hover': {
      backgroundColor: dropdownOptionTokens.colors.surfaceHover,
      color: dropdownOptionTokens.colors.textHover
    },
    '&.Mui-selected': {
      backgroundColor: dropdownOptionTokens.colors.surfaceSelected,
      borderColor: dropdownOptionTokens.colors.borderSelected,
      color: dropdownOptionTokens.colors.textSelected,
      fontWeight: DROPDOWN_OVERLAY.itemSelectedFontWeight,
      '&:hover': {
        backgroundColor: dropdownOptionTokens.colors.surfaceSelected
      }
    },
    '&.Mui-focusVisible': {
      outline: `${dropdownOptionTokens.focusOutlineWidth}px solid ${dropdownOptionTokens.colors.borderFocus}`,
      outlineOffset: dropdownOptionTokens.focusOutlineOffset
    },
    '&.Mui-disabled': {
      color: dropdownOptionTokens.colors.textDisabled,
      opacity: 1
    }
  }
}

type KestrixNavigationOverlayProps = {
  listboxId: string
  isOpen: boolean
  anchorEl: HTMLElement | null
  options: readonly DropdownOption[]
  value: string
  optionSpacing?: DropdownOptionSpacing
  placement?: NavigationMenuPlacement
  onClose: () => void
  onSelect: (value: string) => void
}

export const NavigationOverlay = ({
  listboxId,
  isOpen,
  anchorEl,
  options,
  value,
  optionSpacing = 'cozy',
  placement = 'below',
  onClose,
  onSelect
}: KestrixNavigationOverlayProps) => (
  <Menu
    id={listboxId}
    open={isOpen}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{
      vertical: placement === 'above' ? 'top' : 'bottom',
      horizontal: 'left'
    }}
    transformOrigin={{
      vertical: placement === 'above' ? 'bottom' : 'top',
      horizontal: 'left'
    }}
    slotProps={{ paper: { sx: getOverlayPaperSx(placement) } }}
  >
    {options.map((opt) => {
      if (isDividerOption(opt)) {
        return (
          <Divider
            key={String(opt.value)}
            sx={{ my: 0.5, mx: 1, borderColor: colors.border.light }}
          />
        )
      }

      if (isGroupHeaderOption(opt)) {
        return (
          <ListSubheader
            key={String(opt.value)}
            disableSticky
            data-testid="dropdown-option-group-header"
            sx={{ lineHeight: 1.4, py: 0.5 }}
          >
            {opt.label}
          </ListSubheader>
        )
      }

      const isSelected = opt.value === value
      const spacing = opt.spacing ?? optionSpacing

      return (
        <MenuItem
          key={opt.value}
          selected={isSelected}
          data-testid={opt.testId}
          disabled={opt.disabled}
          onClick={() => onSelect(String(opt.value))}
          aria-selected={isSelected}
          sx={getNavigationMenuItemSx(spacing, isSelected)}
        >
          <DropdownOptionField
            option={opt}
            checked={isSelected}
            isDisabled={!!opt.disabled}
            contentColor={resolveOptionTextColor({
              isSelected,
              isDisabled: !!opt.disabled,
            })}
          />
        </MenuItem>
      )
    })}
  </Menu>
)
